class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initHoverFocusAnnouncement();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
    }

    // 委托绑定（一次）：监听挂 document 捕获阶段，cart-icon-bubble 被 section 重渲染
    // （如购物车页数量更新后 getSectionsToRender 整块替换）后监听不丢失；重复构造幂等
    if (!window.__cartDrawerIconBound) {
      window.__cartDrawerIconBound = true;
      const closestIcon = (target) => (target && target.closest ? target.closest('#cart-icon-bubble') : null);
      document.addEventListener('click', (event) => {
        if (!closestIcon(event.target)) return;
        // 修饰键点击（Cmd/Ctrl/Shift/Alt + 中键）放行 <a> 原生行为，可在新标签页打开 /cart
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        const link = document.querySelector('#cart-icon-bubble');
        if (link) {
          link.setAttribute('role', 'button');
          link.setAttribute('aria-haspopup', 'dialog');
        }
        event.preventDefault();
        this.open(link || undefined);
      }, true);
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.code.toUpperCase() !== 'SPACE') return;
          if (!closestIcon(event.target)) return;
          event.preventDefault();
          this.open(closestIcon(event.target));
        },
        true
      );
    }

    // 慢网守卫（header.liquid 内联脚本）拦截的点击在此消费：脚本就绪后立即打开入队的抽屉
    window.__cartDrawerReady = true;
    if (window.__pendingCartDrawerOpen) {
      window.__pendingCartDrawerOpen = false;
      this.open(cartLink || undefined);
    }
  }

  open(triggeredBy) {
    // 打开抽屉时静默校准存量购物车的赠品价格（后台改折扣后页面仍显示旧价）：
    // touch 触发服务端改价重算，有变化时 __refreshGiftPricing 广播 cartUpdate 刷新抽屉；
    // 60s 控频 + 赠品同步链进行中跳过，加购刚落地（价格本就新鲜）的场景会直接跳过
    if (typeof window.__refreshGiftPricing === 'function') {
      window.__refreshGiftPricing();
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const alreadyOpen = this.classList.contains('active');

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      
      // Extend - Disptch event to refresh Extend
      window.dispatchEvent(new Event('refreshAjaxSideCart'));
      // Extend - End Extend code

      this.classList.add('animate', 'active');
    });

    // If drawer is already open, transitionend won't fire; announce immediately
    if (alreadyOpen && this.pendingOpenAnnouncement) {
      const msg = this.pendingOpenAnnouncement;
      this.pendingOpenAnnouncement = null;
      setTimeout(() => {
        if (typeof window.announceCartStatus === 'function') {
          window.announceCartStatus(msg, null, { assertive: true });
        }
      }, 100);
    }

    this.addEventListener(
      'transitionend',
      () => {
        const drawerContainer = document.getElementById('CartDrawer');
        const emptyContainer = this.querySelector('.drawer__inner-empty');
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? emptyContainer || drawerContainer
          : drawerContainer;
        if (!containerToTrapFocusOn) return;
        const focusElement =
          containerToTrapFocusOn.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close') ||
          containerToTrapFocusOn;

        // Announce with assertive before trapping focus.
        // announceCartStatus has a 150ms internal write delay; we defer trapFocus by 200ms
        // so the live-region text is written (at ~150ms) and starts being read before focus
        // moves into the drawer (at ~200ms). This gives screen readers a 50ms head start on
        // the cart result message. Without the delay, focus moves simultaneously and polite
        // announcements get queued far behind the dialog context speech.
        if (this.pendingOpenAnnouncement && typeof window.announceCartStatus === 'function') {
          const announcement = this.pendingOpenAnnouncement;
          this.pendingOpenAnnouncement = null;
          window.announceCartStatus(announcement, null, { assertive: true });
          setTimeout(() => {
            if (this.classList.contains('active')) {
              trapFocus(containerToTrapFocusOn, focusElement);
            }
          }, 200);
        } else {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
        this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.classList.remove('is-empty');
      this.productId = parsedState.id;
      // 本次渲染使所有在途的旧 section 响应失效（window.__drawerRenderSeq 单调递增），
      // 防止加购前发出的旧响应后到覆盖当前状态（表现为行数量互换）
      window.__drawerRenderSeq = (window.__drawerRenderSeq || 0) + 1;
      // Gift Bag 勾选区在 SRA 渲染中可能缺失（section 设置解析为空）：替换前摘下保留、替换后插回，
      // 随后按购物车 JSON 同步选中态并补水价格/变体 id
      const giftOptionsEntry = this.querySelector('.cart-drawer-gift-options');
      if (giftOptionsEntry) giftOptionsEntry.remove();
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
      if (giftOptionsEntry) {
        const drawerFooter = this.querySelector('.drawer__footer');
        // 勾选区位于 drawer__footer 最顶部（折扣码之前），保留节点插回首位的参照是 footer 的第一个子节点
        if (drawerFooter && !this.querySelector('.cart-drawer-gift-options')) {
          drawerFooter.insertBefore(giftOptionsEntry, drawerFooter.firstChild);
        }
      }
      if (typeof window.__refreshGiftOptionsState === 'function') {
        window.__refreshGiftOptionsState();
      }

    // Store announcement message; it will be read after the drawer opens and focus is trapped
    if (typeof window.getCartAddedMessage === 'function') {
      this.pendingOpenAnnouncement = window.getCartAddedMessage(parsedState);
    } else if (window.cartStrings?.itemAdded) {
      this.pendingOpenAnnouncement = window.cartStrings.itemAdded;
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // 重建出来的 Check Out 按钮不带锁定状态，赠品同步可能仍在进行，需重放一次
      window.cartCheckoutLock?.sync();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!source) return '';
    // 买赠树状分组：SRA 渲染上下文里 product 设置解析为空、服务端分组失效，
    // 在解析文档插入真实 DOM 之前按客户端规则重建分组，避免「先置顶再挪位」的闪烁
    if (source.querySelector('.cart-items tbody') && typeof window.__regroupCartGiftRows === 'function') {
      window.__regroupCartGiftRows(source);
    }
    return source.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'shokz-cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initHoverFocusAnnouncement() {
    const selector = [
      '.quantity__button:not(:disabled)',
      'button.cart-remove-button:not(:disabled)',
    ].join(', ');

    this.addEventListener('pointerover', (event) => {
      if (event.pointerType === 'touch') return;
      if (!this.classList.contains('active')) return;

      const button = event.target.closest(selector);
      if (!button || !this.contains(button)) return;
      if (button.contains(event.relatedTarget)) return;
      if (document.activeElement === button) return;

      button.focus({ preventScroll: true });
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'shokz-cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'CartDrawer-LiveRegionText',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
